/**
 * Dictionary Slice
 *
 * Zustand slice for dictionary state management.
 * Handles active dictionary selection, custom dictionaries (premium),
 * and dictionary loading states.
 * 
 * For Premium/Admin users, the selected dictionary is persisted server-side
 * in the user's profile (preferred_dictionary_id field).
 *
 * @validates Requirements 9.6
 */

import type { StateCreator } from 'zustand';
import { DictionaryType } from '@spellion/shared';
import type { Dictionary } from '@spellion/shared';
import type { StoreState, DictionarySlice, DictionaryState } from './types';

/**
 * Initial dictionary state
 * Note: No default dictionary - admin must import dictionaries first
 */
const initialDictionaryState: DictionaryState = {
  activeDictionary: null,
  availableDictionaries: [],
  customDictionaries: [],
  isLoading: false,
  error: null,
};

/**
 * Create the dictionary slice for the Zustand store
 */
export const createDictionarySlice: StateCreator<
  StoreState,
  [],
  [],
  DictionarySlice
> = (set, get) => ({
  // Initial state
  ...initialDictionaryState,

  // Actions
  setActiveDictionary: (dictionary) => {
    set({
      activeDictionary: dictionary,
      error: null,
    });
    // Note: For Premium/Admin users, the preference is saved server-side
    // via the API call in SearchPage.tsx handleDictionaryChange
  },

  setAvailableDictionaries: (dictionaries) => {
    const { activeDictionary } = get();

    // Update available dictionaries
    set({ availableDictionaries: dictionaries });

    // If the active dictionary is no longer available, reset to first available or null
    if (
      activeDictionary &&
      !dictionaries.some((d) => d.id === activeDictionary.id)
    ) {
      const defaultDict =
        dictionaries.find((d) => d.isDefault) ||
        dictionaries.find((d) => d.type === DictionaryType.SYSTEM) ||
        dictionaries[0] ||
        null;
      set({ activeDictionary: defaultDict });
    }
  },

  setCustomDictionaries: (dictionaries) => {
    set({ customDictionaries: dictionaries });

    // Update available dictionaries to include custom ones
    const { availableDictionaries } = get();
    const systemDicts = availableDictionaries.filter(
      (d) => d.type === DictionaryType.SYSTEM
    );
    set({ availableDictionaries: [...systemDicts, ...dictionaries] });
  },

  addCustomDictionary: (dictionary) => {
    const { customDictionaries, availableDictionaries } = get();

    // Add to custom dictionaries
    const updatedCustom = [...customDictionaries, dictionary];
    set({ customDictionaries: updatedCustom });

    // Add to available dictionaries
    set({ availableDictionaries: [...availableDictionaries, dictionary] });
  },

  removeCustomDictionary: (dictionaryId) => {
    const { customDictionaries, availableDictionaries, activeDictionary } =
      get();

    // Remove from custom dictionaries
    const updatedCustom = customDictionaries.filter(
      (d) => d.id !== dictionaryId
    );
    set({ customDictionaries: updatedCustom });

    // Remove from available dictionaries
    const updatedAvailable = availableDictionaries.filter(
      (d) => d.id !== dictionaryId
    );
    set({ availableDictionaries: updatedAvailable });

    // If the removed dictionary was active, reset to first available or null
    if (activeDictionary?.id === dictionaryId) {
      const defaultDict =
        updatedAvailable.find((d) => d.isDefault) ||
        updatedAvailable.find((d) => d.type === DictionaryType.SYSTEM) ||
        updatedAvailable[0] ||
        null;
      set({ activeDictionary: defaultDict });
    }
  },

  setDictionaryLoading: (isLoading) => set({ isLoading }),

  setDictionaryError: (error) => set({ error, isLoading: false }),

  clearDictionaryError: () => set({ error: null }),

  resetToDefault: () => {
    const { availableDictionaries } = get();
    const defaultDict =
      availableDictionaries.find((d) => d.isDefault) ||
      availableDictionaries.find((d) => d.type === DictionaryType.SYSTEM) ||
      availableDictionaries[0] ||
      null;
    set({ activeDictionary: defaultDict, error: null });
  },

  loadDefaultDictionary: async () => {
    set({ isLoading: true, error: null });
    
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${API_BASE_URL}/dictionaries/default`);
      const data = await response.json();
      
      if (response.ok && data.success && data.data) {
        const dictionary: Dictionary = {
          id: data.data.id,
          name: data.data.name,
          type: data.data.type,
          ownerId: null,
          wordCount: data.data.wordCount,
          isActive: true,
          isDefault: true,
          createdAt: new Date().toISOString(),
        };
        
        set({ 
          activeDictionary: dictionary,
          isLoading: false,
          error: null,
        });
        
        return dictionary;
      } else {
        // No default dictionary available
        set({ 
          activeDictionary: null,
          isLoading: false,
        });
        return null;
      }
    } catch (error) {
      console.error('Failed to load default dictionary:', error);
      // No dictionary available on error
      set({ 
        activeDictionary: null,
        isLoading: false,
      });
      return null;
    }
  },
});
